import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Footer } from '../../components/Footer';
import imgLogowithlcon1 from "../../../assets/logo-cherry-color.webp";
import imgLogoCherry from "../../../assets/logo-cherry-color.webp";
import imgWhiteLogo from "../../../assets/whitelogo.png";
import imgBitmap1 from "../../../assets/homepage_cheerybg.webp";
import imgRectangle2 from "../../../assets/cleaningbg.webp";
import imgRectangle4 from "../../../assets/hircut.webp";
import imgRectangle3 from "../../../assets/beauty.webp";
import imgRectangle5 from "../../../assets/childern.webp";
import imgRectangle7 from "../../../assets/aboutus.webp";
import imgCherrypickedVeticon1 from "../../../assets/verify.png";
import imgCherrypickedBookingicon1 from "../../../assets/bookinginminutes.webp";
import imgCherrypickedClockicon1 from "../../../assets/flexible.webp";
import imgCherrypickedPortfolioicon1 from "../../../assets/browse.webp";
import imgBitmap1Service from "../../../assets/standardclean.webp";
import imgBitmap2Service from "../../../assets/deepcleaan.webp";
import imgBitmap3Service from "../../../assets/endoftenancy.webp";
import { FeaturedReviews } from '../../components/FeaturedReviews';
import { PrimaryButton } from '../../components/PrimaryButton';
import { SecondaryButton } from '../../components/SecondaryButton';
import { LocationAutocomplete } from '../../components/LocationAutocomplete';

import { HeaderPro } from '../../components/HeaderPro';
import {
  formatStartingFromLine,
  getPublicServices,
  type PublicService,
} from '../../../lib/site-api';
import { useSiteImages } from '../../../lib/site-images';

const CLEANING_SERVICE_CODES = [
  'CLEAN_STANDARD',
  'CLEAN_DEEP',
  'CLEAN_END_OF_TENANCY',
] as const;

export default function ProfessionalLandingPage() {
  const navigate = useNavigate();
  const [service, setService] = useState('Cleaning');
  const [address, setAddress] = useState('');
  const [publicServices, setPublicServices] = useState<PublicService[]>([]);
  // Admin-managed landing images (Site Images), with bundled fallbacks.
  const { resolve, heroList } = useSiteImages();
  const heroImages = heroList([imgBitmap1]);
  const [heroIndex, setHeroIndex] = useState(0);
  useEffect(() => {
    setHeroIndex(0);
    if (heroImages.length < 2) return;
    const id = setInterval(
      () => setHeroIndex((i) => (i + 1) % heroImages.length),
      6000,
    );
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heroImages.length]);

  const priceLineFor = (code: string, fallback: string): string => {
    const svc = publicServices.find((s) => s.code === code);
    return svc ? formatStartingFromLine(svc) : fallback;
  };
  const cleaningStartingPrice = (fallback: string): string => {
    const rates = CLEANING_SERVICE_CODES.map(
      (c) => publicServices.find((s) => s.code === c)?.starting_from_price,
    ).filter((v): v is number => typeof v === 'number');
    if (rates.length === 0) return fallback;
    const min = Math.min(...rates);
    const display = min % 1 === 0 ? min.toFixed(0) : min.toFixed(2);
    return `Starting from £${display}/hr`;
  };

  useEffect(() => {
    let cancelled = false;
    getPublicServices()
      .then((res) => {
        if (!cancelled) setPublicServices(res.data ?? []);
      })
      .catch(() => {
        /* fall back to static "Starting from" lines */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleBookNow = () => {
    navigate('/booking');
  };

  return (
    <div className=" bg-white">
      {/* Navigation Bar */}
      <HeaderPro />

      {/* Header/Hero Section */}
      <header className="relative overflow-hidden bg-[#8e2a2f] flex items-center justify-center py-5 pt-20 md:pt-30 md:pb-20 px-4">
        {/* Background slideshow — admin-managed hero slides, crossfading. */}
        {heroImages.map((src, i) => (
          <div
            key={`${src}-${i}`}
            aria-hidden
            className="absolute inset-0 bg-cover bg-center transition-opacity duration-1000 ease-in-out"
            style={{ backgroundImage: `url(${src})`, opacity: i === heroIndex % heroImages.length ? 1 : 0 }}
          />
        ))}
        {/* Overlay */}
        <div className="absolute inset-0 bg-black/20" />

        {/* Hero Content - Centered Card */}
        <div className="relative z-10 w-full max-w-[555px] mx-auto">
          <div className="bg-[#8e2a2f]/80 rounded-[12px] p-6 md:p-12 shadow-2xl w-full min-h-[500px] md:min-h-[579px] flex flex-col items-center">
            {/* Logo */}
            <div className="flex justify-center mb-3">
              <div className="w-[185px]">
                <img src={imgWhiteLogo} alt="Cherry Picked" className="w-full h-auto object-contain" />
              </div>
            </div>

            {/* Title */}
            <h1 className="font-['Playfair_Display',sans-serif] font-bold text-[#fefbf2] text-[26px] md:text-[34px] text-center leading-[1.1] tracking-[-0.2px] mb-6 md:mb-8">
              Book trusted professionals in just a few clicks.
            </h1>

            {/* Features - 2 rows */}
            <div className="w-full space-y-4 mb-5">
              {/* Row 1 */}
              <div className="flex flex-wrap items-center justify-center gap-3 md:gap-6">
                {[
                  { text: 'ID Verified' },
                  { text: 'Real-time availability' },
                  { text: 'Insured' },
                ].map((feature, index) => (
                  <div key={index} className="flex items-center gap-1.5">
                    <span className="material-icons text-white !text-[14px] md:!text-[16px]">check_circle_outline</span>
                    <span className="font-['DM_Sans',sans-serif] text-white text-[12px] md:text-sm whitespace-nowrap">{feature.text}</span>
                  </div>
                ))}
              </div>
              {/* Row 2 */}
              <div className="flex flex-wrap items-center justify-center gap-3 md:gap-6">
                {[
                  { text: 'Evenings & weekends' },
                  { text: 'Book with confidence' },
                ].map((feature, index) => (
                  <div key={index} className="flex items-center gap-1.5">
                    <span className="material-icons text-white !text-[14px] md:!text-[16px]">check_circle_outline</span>
                    <span className="font-['DM_Sans',sans-serif] text-white text-[12px] md:text-sm whitespace-nowrap">{feature.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Form Fields */}
            <div className="w-full space-y-4">
              {/* Service Select (Floating Style) */}
              <div className="relative">
                <div className="h-[64px] bg-white rounded-[10px] border border-[#eee] flex flex-col justify-center px-5 pt-3">
                  <label className="absolute left-5 top-[12px] text-[10px] text-[#908e8e] font-['DM_Sans',sans-serif]">
                    Select a service
                  </label>
                  <select
                    value={service}
                    onChange={(e) => setService(e.target.value)}
                    className="w-full bg-transparent border-none outline-none font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[14px] appearance-none cursor-pointer"
                  >
                    <option>Cleaning</option>
                    <option>Hair</option>
                    <option>Beauty & Wellness</option>
                    <option>Childcare</option>
                  </select>
                  <span className="absolute right-5 top-1/2 -translate-y-1/2 mt-2 material-icons text-[#3a3335] pointer-events-none">expand_more</span>
                </div>
              </div>

              {/* Address Input (Floating Style) */}
              <LocationAutocomplete
                label="Enter your full address / postcode"
                value={address}
                onChange={(value) => setAddress(value)}
                placeholder="45 West Street East Central London EC59 8FB"
                className="w-full"
                containerHeight="h-[64px]"
                icon="location_on"
              />

              {/* Book Now Button */}
              <button
                onClick={handleBookNow}
                className="w-full h-[54px] bg-[#f2eee2] hover:bg-[#ebe5d5] text-[#8e2a2f] font-['DM_Sans',sans-serif] font-bold text-[14px] rounded-[8px] transition-colors mt-2"
              >
                Book now
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Features Section */}
      <section className="pt-[50px] pb-[50px] bg-[#f2eee2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: imgCherrypickedVeticon1,
                title: 'Vetted Professionals',
                desc: ['Carefully selected experts you', 'can trust.']
              },
              {
                icon: imgCherrypickedBookingicon1,
                title: 'Book in minutes',
                desc: ['Find and secure appointments', 'quickly.']
              },
              {
                icon: imgCherrypickedClockicon1,
                title: 'Flexible Appointments',
                desc: ['Choose times that fit around', 'your schedule.']
              },
              {
                icon: imgCherrypickedPortfolioicon1,
                title: 'Browse Portfolios & Reviews',
                desc: ['See previous work and real', 'customer feedback.']
              },
            ].map((feature, index) => (
              <div key={index} className="bg-white rounded-[6px] shadow-[0px_3px_4px_0px_rgba(153,155,168,0.15)] p-8 text-center">
                <div className="w-[51px] h-[51px] mx-auto mb-6">
                  <img src={feature.icon} alt={feature.title} className="w-full h-full object-contain" />
                </div>
                <h3 className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-lg leading-[27px] mb-3">
                  {feature.title}
                </h3>
                <div className="font-['DM_Sans',sans-serif] text-[#3a3335] text-sm leading-[21px] tracking-[-0.1px]" style={{ fontVariationSettings: "'opsz' 14" }}>
                  {feature.desc.map((line, i) => (
                    <p key={i} className={i < feature.desc.length - 1 ? 'mb-px' : ''}>
                      {line}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-5 md:py-0 bg-[#f2eee2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              { img: resolve('category_cleaning', imgRectangle2), title: 'Cleaning', desc: 'Domestic cleaning including standard clean, deep clean and end of tenancy.', price: cleaningStartingPrice('Starting from £21.00/hr') },
              { img: resolve('category_hair', imgRectangle4), title: 'Hair', desc: 'At-home hair services.', price: '10% off with Cherry Picked membership' },
              { img: resolve('category_beauty', imgRectangle3), title: 'Beauty & Wellness', desc: 'At-home beauty and wellness treatments.', price: '10% off with Cherry Picked membership' },
              { img: resolve('category_childcare', imgRectangle5), title: 'Childcare', desc: 'At-home childcare services.', price: '10% off with Cherry Picked membership' },
            ].map((service, index) => (
              <div key={index} className="relative h-[470px] overflow-hidden group cursor-pointer bg-[#d9d9d9]">
                <img src={service.img} alt={service.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-white" style={{ top: '62px' }} />
                <div className="absolute bottom-0 left-0 right-0 text-center px-8 pb-16">
                  <h3 className="font-['Playfair_Display',sans-serif] font-extrabold text-[34px] leading-[48px] tracking-[-0.0944px] text-[#8e2a2f] mb-3">
                    {service.title}
                  </h3>
                  <div className="font-['DM_Sans',sans-serif] text-sm leading-[21px] tracking-[-0.1px] text-[#3a3335] mb-6" style={{ fontVariationSettings: "'opsz' 14" }}>
                    <p className="mb-px">{service.desc}</p>
                    <p>{service.price}</p>
                  </div>
                  <PrimaryButton
                    onClick={() => navigate('/service-place')}
                    className="!w-[151px] !h-[40px] !text-xs !uppercase !tracking-wider !rounded-[4px] mx-auto"
                  >
                    BOOK NOW
                  </PrimaryButton>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews Section — admin-curated featured reviews */}
      <FeaturedReviews />

      {/* About Section */}
      <section className=" overflow-hidden bg-[#f2eee2]  py-16 md:py-16">
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {/* Left: Image (Square corners) */}
            <div className="h-[400px] lg:h-auto overflow-hidden">
              <img src={resolve('about_us', imgRectangle7)} alt="About us" className="w-full h-full object-cover" />
            </div>

            {/* Right: Content (Centered) */}
            <div className="flex items-center justify-center py-16 px-8 md:px-16 lg:px-24 bg-white">
              <div className="max-w-[420px] text-center">
                {/* Logo */}
                <div className="flex justify-center mb-6">
                  <img src={imgLogoCherry} alt="Cherry Picked" className="h-[70px] object-contain" />
                </div>
                <h2 className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[34px] md:text-[40px] leading-tight mb-6">
                  About us
                </h2>
                <p className="font-['DM_Sans',sans-serif] text-[#3a3335] text-[15px] leading-relaxed" style={{ fontVariationSettings: "'opsz' 14" }}>
                  Cherry Picked was founded by three busy mums who understood first-hand the challenge of balancing family life, careers, home and self-care. Between packed schedules and endless to-do lists, we saw the need for a simpler, more reliable way to access exceptional local services.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Popular at-home services Section — admin-curated. Admins flag up
          to 3 services via the "Popular" toggle on the admin Services
          list; the band renders those (in sort order) and hides entirely
          when none are flagged. */}
      {(() => {
        const popularServices = publicServices
          .filter((s) => s.is_popular)
          .slice(0, 3);
        if (popularServices.length === 0) return null;
        return (
          <section className="relative bg-[#f2eee2] pt-[120px] pb-[50px]">
            {/* Burgundy Background Shape */}
            <div className="absolute top-0 left-0 right-0 h-[245px] bg-[#8e2a2f] overflow-hidden">
              <div className="absolute inset-0" style={{
                background: '#8e2a2f',
                clipPath: 'polygon(0 0, 100% 0, 100% 60%, 0 100%)'
              }} />
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
              {/* Header */}
              <div className="flex items-center justify-between mb-12">
                <h2 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#eee] text-[34px] leading-[48px] tracking-[-0.0944px]">
                  Popular at-home services
                </h2>
              </div>

              {/* Cards — each is a flagged service: its own catalogue
                  image, name and live "from" price. */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {popularServices.map((svc) => {
                  const img =
                    svc.image_preview_url ?? svc.image ?? imgBitmap1Service;
                  return (
                    <div key={svc.id} className="bg-white rounded-[6px] shadow-[0px_3px_4px_0px_rgba(153,155,168,0.15)] overflow-hidden">
                      <div className="relative h-[188px] bg-white overflow-hidden">
                        <div className="absolute inset-0 bg-black/20" />
                        <img src={img} alt={svc.name} className="absolute inset-0 w-full h-full object-cover" />
                      </div>
                      <div className="p-6">
                        <h3 className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-lg leading-[27px] mb-1">
                          {svc.name}
                        </h3>
                        <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-sm leading-[21px] tracking-[-0.1px]" style={{ fontVariationSettings: "'opsz' 14" }}>
                          {formatStartingFromLine(svc)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        );
      })()}

      <Footer />
    </div>
  );
}